import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQuery, useMutation } from '@apollo/client';

// material
import { LoadingButton } from '@mui/lab';
import { Box, Stack, IconButton, InputAdornment, InputLabel, FormControl } from '@mui/material';

// graphql
import { RegisterUserGQLFunction } from '../../graphql/REGISTER_USER';
// hooks
import useResponsive from '../../hooks/useResponsive';
// components
import { FormProvider, RHFTextField, RHFToggleButton, RHFSelect } from '../../components/hook-form';

// info
import { USER } from '../../info/USER';

export default function EditRegisterUserForm({ registerUserId }) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const smUp = useResponsive('up', 'sm');
  const navigate = useNavigate();

  const EditUserSchema = Yup.object().shape({
    userId: Yup.string().required(t('people.userid_req')),
    authenticator: Yup.string().email(t('login.email_valid')).required(t('people.identity_req')),
    username: Yup.string().required(t('people.username_req')),
    authCode: Yup.string().min(6, t('people.auth_code_len')).max(6, t('people.auth_code_len')),
  });

  const defaultValues = {
    id: '',
    userId: '',
    authenticator: '',
    username: '',
    nickname: '',
    title: '',
    authCode: '',
    userType: '',
  };

  const methods = useForm({
    resolver: yupResolver(EditUserSchema),
    defaultValues,
  });
  const {
    handleSubmit,
    formState: { isSubmitting },
    reset,
    setError,
  } = methods;

  const { loading: queryLoading, data: queryData } = useQuery(RegisterUserGQLFunction.GET_REGISTER_USER_INFO, {
    variables: { id: registerUserId },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (queryData && queryData.registerUserInfo) {
      const info = queryData.registerUserInfo;
      reset({
        id: info.id || '',
        userId: info.userId || '',
        authenticator: info.authenticator || '',
        username: info.username || '',
        nickname: info.nickname || '',
        title: info.title || '',
        authCode: info.authCode || '',
        userType: info.userType || 0,
      });
    }
  }, [queryData, reset]);

  const [updateRegisterUser] = useMutation(RegisterUserGQLFunction.UPDATE_REGISTER_USER);

  const onSubmit = async (input) => {
    try {
      const { data } = await updateRegisterUser({
        variables: {
          id: registerUserId,
          userId: input.userId,
          authenticator: input.authenticator,
          username: input.username,
          nickname: input.nickname,
          title: input.title,
          userType: input.userType,
          authCode: input.authCode,
        },
      });

      const info = data.updateRegisterUser;
      if (info.err) {
        setError(info.err, { type: 'custom', message: info.msg });
      } else {
        enqueueSnackbar(t('common.success'), { variant: 'success' });
        navigate(`/register/info/${registerUserId}`, { replace: true });
      }
    } catch (error) {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Box
        sx={{
          display: 'grid',
          columnGap: 2,
          rowGap: 3,
          gridTemplateColumns: smUp ? 'repeat(2, 1fr)' : '1fr',
          mt: 1,
          mb: 4,
        }}>
        <RHFTextField name="userId" label={t('common.user_id')} loading={queryLoading} />
        <RHFTextField name="authenticator" label={t('common.email')} loading={queryLoading} />
        <RHFTextField name="username" label={t('common.username')} loading={queryLoading} />
        <RHFTextField name="nickname" label={t('common.nickname')} loading={queryLoading} />
        <RHFTextField name="title" label={t('common.title')} loading={queryLoading} />
        <RHFTextField name="authCode" label={t('people.auth_code')} loading={queryLoading} />

        {smUp ? (
          <FormControl sx={{ gridColumn: 'span 2' }}>
            <Box sx={{ mb: 2 }}>
              <InputLabel shrink>{t('common.user_type')}</InputLabel>
            </Box>
            <RHFToggleButton states={USER.USER_TYPE.map(type => t(`common.${type.toLowerCase()}`))} name="userType" loading={queryLoading} />
          </FormControl>
        ) : (
          <FormControl>
            <RHFSelect label={t('common.user_type')} states={USER.USER_TYPE.map(type => t(`common.${type.toLowerCase()}`))} name="userType" loading={queryLoading} />
          </FormControl>
        )}
      </Box>
      <LoadingButton
        sx={{ maxWidth: '200px' }}
        fullWidth
        type="submit"
        variant="contained"
        loading={isSubmitting}>
        {t('common.save')}
      </LoadingButton>
    </FormProvider>
  );
}
